import React, { useContext, useState } from "react";
import * as Yup from "yup";
import { DateTime } from "luxon";
import { Formik, FormikProps } from "formik";
import { Redirect, useHistory, useParams } from "react-router-dom";
import { ErrorFragmentFragment, useGroupQuery, useUpdateGroupMutation } from "generated/graphql";
import { CommonContext } from "config/commonProvider";
import { getBUFromLocalStorage } from "helpers/storage";
import { handleErrorCheck, notNothing } from "helpers/general";
import { useGetAnimalTypes, useStateSpecies } from "hooks";
import {
  Breadcrumbs,
  FieldDate,
  FieldNumber,
  FieldSelect,
  FieldText,
  Flex,
  LoadingOverlay,
  PageContent,
  PageHeader,
  Spacer,
  Title,
} from "components";
import GroupHeader from "pages/MyLivestock/General/GroupFieldHeader";
import { GROUPS, GROUP_VIEW } from "constants/Routes";
import { GROUP_CATEGORY_OPTIONS } from "constants/Groups";
import { GroupAnimalsList } from "groups/GroupAnimalsList";
import { HEADER_TYPES } from "constants/General";

import styles from "pages/MyLivestock/Groups/CreateEditGroup/CreateEditGroup.module.scss";

type FormValues = {
  name: string;
  targetWeight: string;
  targetDate: Date | string;
  category: string;
};

type FormProps = FormikProps<FormValues>;

const validationSchemaCreateGroup = Yup.object().shape({
  name: Yup.string().required("Group name is required!"),
  targetWeight: Yup.string().required("Target weight name is required!"),
  targetDate: Yup.string(),
  category: Yup.string().required("Type is required!"),
});

export const EditGroup: React.FC = () => {
  const [addingAnimalsToGroup, setAddingAnimalsToGroup] = useState<boolean>(false);
  const { active: speciesActive } = useStateSpecies();
  const businessUnitId = getBUFromLocalStorage();

  const { groupId } = useParams<LivestockGroupPageParams>();

  const { data: groupsData, loading: getGroupLoading } = useGroupQuery({
    variables: {
      group: groupId,
      businessUnit: businessUnitId,
    },
  });

  const [updateGroupMutation, { loading: updateGroupLoading }] = useUpdateGroupMutation();

  const history = useHistory();

  const { showNotification } = useContext(CommonContext);
  const { data: animalTypesData } = useGetAnimalTypes();

  const group = groupsData?.group;
  const name = group?.name || "";
  const targetWeight = group?.targetWeight ? `${group?.targetWeight}` : "";
  const category = group?.category || GROUP_CATEGORY_OPTIONS[3].value;
  const animalTypes = animalTypesData?.animalTypes;
  const animalTypeCattle = animalTypes?.find((item) => item?.slug === "cattle");
  const targetDate = group?.targetDate ? DateTime.fromISO(group.targetDate).toJSDate() : "";

  const showActionResult = (errors: ErrorFragmentFragment[]): void => {
    if (errors.length > 0) {
      showNotification({
        variant: "error",
        message: errors.map((item) => item?.message).join("; "),
      });
    } else {
      history.push(`${GROUP_VIEW}/${group?.id}`);
      showNotification({
        message: "Group successfully updated.",
      });
    }
  };

  const handleSaveGroup = async (values: FormValues): Promise<void> => {
    const input = {
      name: values.name,
      targetWeight: Number(values.targetWeight),
      targetDate: typeof values.targetDate !== "string" ? DateTime.fromJSDate(values.targetDate).toISODate() : null,
      animalType: Number(speciesActive?.id || animalTypeCattle?.id),
      category: values.category,
    };

    try {
      const { data } = await updateGroupMutation({
        variables: {
          input: {
            ...input,
            id: groupId,
          },
        },
      });

      const errors = data?.updateGroup?.errors?.filter(notNothing) ?? [];

      showActionResult(errors);
    } catch (e) {
      showNotification({
        variant: "error",
        message: "Error updating group",
      });
    }
  };

  const initialValues = {
    name,
    targetWeight,
    category,
    targetDate,
  };

  if (speciesActive) {
    const isGroupDifferentSpecies = group && group.animalType?.id !== speciesActive.id;
    if (isGroupDifferentSpecies) return <Redirect to={GROUPS} />;
  }

  return (
    <>
      {(getGroupLoading || updateGroupLoading) && <LoadingOverlay customStyles={styles.loadingOverlay} />}

      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchemaCreateGroup}
        onSubmit={handleSaveGroup}
      >
        {({ values, errors, handleChange, setFieldValue, handleSubmit }: FormProps): React.ReactNode => {
          const handleTargetDateChange = (value: string): void => {
            setFieldValue("targetDate", value);
          };

          return (
            <>
              <PageHeader>
                <Breadcrumbs
                  rootPaths={[
                    {
                      path: "Groups",
                      route: GROUPS,
                    },
                  ]}
                  currentPath={group ? "Update Group" : "Create Group"}
                />

                <GroupHeader type={HEADER_TYPES.GROUP} item={group} handleSubmit={handleSubmit} />
              </PageHeader>

              <PageContent>
                <Spacer baselineHeight={3} />

                <Flex container>
                  <Flex item itemGutter xs={12}>
                    <Title secondary>Group details</Title>
                  </Flex>
                </Flex>
                <Spacer baselineHeight={1} />
                <Flex container>
                  <Flex item itemGutter xs={12} s={6} m={4} l={3} xl={2}>
                    <FieldText
                      label="Group name"
                      error={errors.name}
                      inputProps={{
                        name: "name",
                        defaultValue: values.name,
                        onChange: handleChange,
                      }}
                    />
                  </Flex>

                  <Flex item itemGutter xs={12} s={6} m={4} l={3} xl={2}>
                    <FieldNumber
                      label="Target weight"
                      error={handleErrorCheck(errors.targetWeight)}
                      inputProps={{
                        name: "targetWeight",
                        onChange: handleChange,
                        value: values.targetWeight,
                      }}
                    />
                  </Flex>
                  <Flex item itemGutter xs={12} s={6} m={4} l={3} xl={2}>
                    <FieldDate
                      label="Target Date"
                      error={handleErrorCheck(errors.targetDate)}
                      inputProps={{
                        name: "targetDate",
                        value: values.targetDate,
                        onChange: handleTargetDateChange,
                        minDate: new Date(),
                      }}
                    />
                  </Flex>
                  <Flex item itemGutter xs={12} s={6} m={4} l={3} xl={2}>
                    <FieldSelect
                      label="Type"
                      error={handleErrorCheck(errors.category)}
                      inputProps={{
                        name: "category",
                        value: values.category,
                        placeholder: "Select type",
                        options: GROUP_CATEGORY_OPTIONS,
                        onChange: handleChange,
                        valueKey: "value",
                        labelKey: "label",
                      }}
                    />
                  </Flex>
                </Flex>

                <Spacer baselineHeight={3} />

                <GroupAnimalsList
                  groupId={groupId}
                  addingAnimalsToGroup={addingAnimalsToGroup}
                  setAddingAnimalsToGroup={setAddingAnimalsToGroup}
                />
              </PageContent>
            </>
          );
        }}
      </Formik>
    </>
  );
};
